import { useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Plane, Headphones, Loader2, Pause, User, Menu, X, ChevronDown } from "lucide-react";
import axios from "axios";
import TranslatePage from "../TranslatePage";
import { useAuth } from "../../context/AuthContext";
import { detectBrand } from "../../lib/brand";

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = detectBrand();

/* AirlinesAirports.com site shell — global header, nav and footer.
   Design: white header, navy brand, dropdown nav (per user mockup). */

const NAV_GLOBAL = [
  { label: "Airlines", items: [["All Airlines", "/airlines"], ["Directory A–Z", "/airlines/directory"], ["Global Ranking", "/airlines/ranking"]] },
  { label: "Airports", items: [["All Airports", "/airports"], ["Directory A–Z", "/airports/directory"], ["World's Busiest", "/airports/ranking"], ["India Airports", "/india"]] },
  { label: "Routes", to: "/map" },
  { label: "Flight Status", to: "/my-flight" },
  { label: "Guides", to: "/guides" },
  { label: "News", to: "/aviation-news" },
  { label: "Search", to: "/search" },
];

// India domain — richer nav, split into two rows for readability.
const NAV_INDIA_PRIMARY = [
  { label: "Airports", items: [["All Airports", "/airports"], ["By State / UT", "/airports?view=states"], ["International", "/airports?type=international"], ["Domestic", "/airports?type=domestic"], ["Airport Map", "/map"], ["Travel to India", "/travel-to-india"], ["Festival Advisor", "/travel-to-india/festivals"]] },
  { label: "Airlines", to: "/india/airlines" },
  { label: "Flights", to: "/flight-search" },
  { label: "Routes", to: "/map" },
  { label: "Destinations", to: "/destinations" },
];
const NAV_INDIA_SECONDARY = [
  { label: "Transport", items: [["Taxi & Metro", "/ground"], ["Rail", "/rail"], ["Bus", "/bus"], ["Rideshare", "/rideshare"]] },
  { label: "Stay", to: "/stays" },
  { label: "Dining", to: "/food-dining" },
  { label: "Lounges", to: "/airports/directory" },
  { label: "Services", to: "/marketplace" },
  { label: "Deals", to: "#ia-deals", badge: "NEW" },
  { label: "News", to: "/aviation-news" },
  { label: "Guides", to: "/guides" },
  { label: "Community", to: "/community" },
  { label: "Business", to: "/my-business" },
  { label: "AI Assistant", to: "#ask-ai" },
];

const NAV = BRAND.isIndia ? [...NAV_INDIA_PRIMARY, ...NAV_INDIA_SECONDARY] : NAV_GLOBAL;

export function AAHeader() {
  const [open, setOpen] = useState(false);
  const [ttsState, setTtsState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const { user } = useAuth();

  const fallbackBrowserTTS = (text) => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (synth.speaking) { synth.cancel(); setTtsState("idle"); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.98;
    u.onend = () => setTtsState("idle");
    synth.speak(u);
    setTtsState("playing");
  };

  const listen = async () => {
    // If already playing, pause.
    if (ttsState === "playing") {
      if (audioRef.current && !audioRef.current.paused) audioRef.current.pause();
      if (window.speechSynthesis?.speaking) window.speechSynthesis.cancel();
      setTtsState("idle");
      return;
    }
    const text = (document.querySelector("main")?.innerText || "").replace(/\s+/g, " ").trim().slice(0, 3800);
    if (text.length < 20) return;
    setTtsState("loading");
    try {
      const r = await axios.post(`${API}/api/tts/generate`, { text, voice: "nova", speed: 1.0 });
      if (audioRef.current) {
        audioRef.current.src = `${API}${r.data.url}`;
        audioRef.current.play();
        setTtsState("playing");
      }
    } catch {
      fallbackBrowserTTS(text);
    }
  };

  // Reusable nav link renderer (used by both single-row global nav and dual-row India nav)
  const renderNavItem = (n, sizeCls) => n.items ? (
    <div key={n.label} className="relative group">
      <button data-testid={`aa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`inline-flex items-center gap-1 font-700 text-zinc-800 hover:text-blue-700 rounded-md transition-colors ${sizeCls}`}>
        {n.label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50">
        <div className="bg-white border border-zinc-200 rounded-lg shadow-lg py-1.5 min-w-[190px]">
          {n.items.map(([label, to]) => (
            <Link key={to} to={to} data-testid={`aa-nav-sub-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="block px-4 py-2 text-[13px] font-600 text-zinc-600 hover:text-blue-700 hover:bg-blue-50 transition-colors">{label}</Link>
          ))}
        </div>
      </div>
    </div>
  ) : n.to?.startsWith("#") ? (
    <a key={n.label} href={n.to} data-testid={`aa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative font-700 rounded-md transition-colors text-zinc-800 hover:text-blue-700 ${sizeCls}`}>
      {n.label}
      {n.badge && <span className="ml-1.5 inline-block bg-red-500 text-white text-[9px] font-800 uppercase tracking-wide rounded-full px-1.5 py-0.5 align-middle">{n.badge}</span>}
    </a>
  ) : (
    <NavLink key={n.label} to={n.to} data-testid={`aa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `relative font-700 rounded-md transition-colors ${isActive ? "text-blue-700" : "text-zinc-800 hover:text-blue-700"} ${sizeCls}`}>
      {n.label}
      {n.badge && <span className="ml-1.5 inline-block bg-red-500 text-white text-[9px] font-800 uppercase tracking-wide rounded-full px-1.5 py-0.5 align-middle">{n.badge}</span>}
    </NavLink>
  );

  return (
    <header className="border-b border-zinc-200 bg-white sticky top-0 z-40" data-testid="aa-header">
      {/* Row 1 — logo + language + listen + account */}
      <div className="max-w-7xl mx-auto px-2 sm:px-5 py-2.5 flex items-center gap-1 sm:gap-4 overflow-x-clip">
        <Link to="/" className="flex items-center gap-1.5 max-[360px]:gap-1 sm:gap-2.5 shrink min-w-0" data-testid="aa-logo">
          <span className="inline-flex p-1.5 sm:p-2 rounded-full bg-[#0A2A5E] text-white shrink-0"><Plane className="w-4 h-4 sm:w-5 sm:h-5 -rotate-45" /></span>
          <span className="min-w-0 whitespace-nowrap">
            <span className="notranslate font-heading font-900 text-sm max-[360px]:text-[13px] sm:text-xl leading-none whitespace-nowrap text-[#0A2A5E] block">{BRAND.name}<span className="text-blue-600">.com</span></span>
            <span className="hidden sm:block text-[11px] text-zinc-500 font-600 whitespace-nowrap truncate">{BRAND.tagline}</span>
          </span>
        </Link>

        {/* Global (non-India) — nav lives on this row */}
        {!BRAND.isIndia && (
          <nav className="hidden lg:flex items-center gap-1.5 mx-auto" data-testid="aa-nav">
            {NAV_GLOBAL.map((n) => renderNavItem(n, "text-[15px] px-3 py-2"))}
          </nav>
        )}

        <div className={`${BRAND.isIndia ? "ml-auto" : "ml-auto lg:ml-0"} flex items-center gap-0.5 sm:gap-2 text-sm min-w-0 shrink-0`}>
          <span className="block" data-testid="aa-translate"><TranslatePage /></span>
          <button onClick={listen} data-testid="aa-listen-btn" aria-label="Listen to this page"
            disabled={ttsState === "loading"}
            title="Listen to this page"
            className="inline-flex items-center gap-1.5 font-800 text-zinc-700 hover:text-blue-700 px-1.5 max-[360px]:px-0.5 sm:px-3 py-1.5 sm:py-2 rounded-md disabled:opacity-70 transition-colors border border-transparent hover:border-blue-200 hover:bg-blue-50">
            {ttsState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> :
             ttsState === "playing" ? <Pause className="w-4 h-4" /> :
             <Headphones className="w-4 h-4" />}
            <span className="hidden md:inline text-[13px]">
              {ttsState === "loading" ? "Loading…" : ttsState === "playing" ? "Pause" : "Listen"}
            </span>
            <span className="hidden md:inline text-blue-500 opacity-70 text-[13px] font-800">&gt;</span>
          </button>
          <audio ref={audioRef} onEnded={() => setTtsState("idle")} onPause={() => setTtsState("idle")} preload="none" />
          {user ? (
            <Link to="/travel-hub?src=%2Faa-header" data-testid="aa-account" aria-label="My account"
              className="inline-flex items-center font-700 text-blue-800 px-1 sm:px-1.5 py-1.5">
              <span className="w-7 h-7 rounded-full bg-[#0A2A5E] text-white text-[12px] font-800 inline-flex items-center justify-center">
                {(user.name || user.email || "T")[0].toUpperCase()}
              </span>
            </Link>
          ) : (
            <Link to="/login" data-testid="aa-signin" aria-label="Sign in"
              className="inline-flex items-center gap-1.5 font-700 text-zinc-700 hover:text-blue-700 px-1.5 sm:px-2.5 py-1.5 sm:py-2 rounded-md hover:bg-blue-50">
              <User className="w-4 h-4" /><span className="hidden lg:inline text-[13px]">Sign In</span>
            </Link>
          )}
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-1 max-[360px]:p-0.5 text-zinc-600 shrink-0" data-testid="aa-nav-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* India domain — Row 2 primary nav (larger) + Row 3 secondary nav */}
      {BRAND.isIndia && (
        <>
          <nav className="hidden lg:block border-t border-zinc-100" data-testid="aa-nav-primary">
            <div className="max-w-7xl mx-auto px-5 py-2 flex flex-wrap items-center gap-x-6 gap-y-1">
              {NAV_INDIA_PRIMARY.map((n) => renderNavItem(n, "text-[16px] font-800 px-1 py-1.5"))}
            </div>
          </nav>
          <nav className="hidden lg:block border-t border-zinc-100 bg-zinc-50/70" data-testid="aa-nav-secondary">
            <div className="max-w-7xl mx-auto px-5 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1">
              {NAV_INDIA_SECONDARY.map((n) => renderNavItem(n, "text-[14px] font-700 px-1 py-1"))}
            </div>
          </nav>
        </>
      )}

      {/* Mobile nav */}
      {open && (
        <nav className="lg:hidden border-t border-zinc-100 bg-white" data-testid="aa-nav-mobile">
          <div className="max-w-7xl mx-auto px-5 py-2 flex flex-col">
            {NAV.map((n) => n.items ? (
              <div key={n.label} className="py-1">
                <div className="text-[11px] font-800 uppercase tracking-wide text-zinc-400 px-1 pt-2">{n.label}</div>
                {n.items.map(([label, to]) => (
                  <Link key={to} to={to} onClick={() => setOpen(false)}
                    className="block px-3 py-2 text-sm font-700 text-zinc-700 hover:text-blue-700 rounded-md hover:bg-blue-50">{label}</Link>
                ))}
              </div>
            ) : (
              <NavLink key={n.label} to={n.to} onClick={() => setOpen(false)}
                className="px-1 py-2.5 text-sm font-800 text-zinc-700 hover:text-blue-700 border-b border-zinc-50">{n.label}</NavLink>
            ))}
            <Link to="/travel-hub" onClick={() => setOpen(false)} className="px-1 py-2.5 text-sm font-800 text-blue-800">My Travel Hub</Link>
            <Link to="/get-app" onClick={() => setOpen(false)} className="px-1 py-2.5 text-sm font-800 text-zinc-700">Download App</Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function AAFooter() {
  return (
    <footer className="border-t border-zinc-200 bg-zinc-50 mt-10" data-testid="aa-footer">
      <div className="max-w-7xl mx-auto px-5 py-8 flex flex-wrap items-start gap-8">
        <div className="max-w-xs">
          <div className="font-heading font-900 text-lg text-[#0A2A5E] notranslate">{BRAND.name}<span className="text-blue-600 text-xs">.com</span></div>
          <p className="text-xs text-zinc-500 mt-1">{BRAND.tagline} The world's most comprehensive resource for airlines, airports and routes.</p>
          <p className="text-[11px] text-zinc-400 mt-2">Part of the <a href="https://travelerpulse.pro" target="_blank" rel="noopener noreferrer" className="font-700 text-blue-700 hover:underline">TravelerPulse</a> network</p>
        </div>
        <div className="text-xs space-y-1.5">
          <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Explore</div>
          <Link to="/airports" className="block text-zinc-600 hover:text-blue-700">Airports Directory</Link>
          <Link to="/airlines" className="block text-zinc-600 hover:text-blue-700">Airlines Directory</Link>
          <Link to="/aircraft" className="block text-zinc-600 hover:text-blue-700">Aircraft</Link>
          <Link to="/map" className="block text-zinc-600 hover:text-blue-700">Route Map</Link>
          <Link to="/aviation-news" className="block text-zinc-600 hover:text-blue-700">Aviation News</Link>
          <Link to="/india" className="block text-zinc-600 hover:text-blue-700">IndianAirports Platform</Link>
        </div>
        <div className="text-xs space-y-1.5">
          <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Plan</div>
          <Link to="/flight-search" className="block text-zinc-600 hover:text-blue-700">Flight Search</Link>
          <Link to="/plan-ai" className="block text-zinc-600 hover:text-blue-700">AI Trip Planner</Link>
          <Link to="/travel-hub" className="block text-zinc-600 hover:text-blue-700">My Travel Hub</Link>
          <Link to="/get-app" className="block text-zinc-600 hover:text-blue-700">Download App</Link>
        </div>
        <p className="text-[11px] text-zinc-400 ml-auto max-w-[240px]">Aviation data changes — every record carries a source and last-verified date. Always confirm critical details with your airline. For airports and airlines outside India, visit <a href="https://www.airlinesairports.com" target="_blank" rel="noopener noreferrer" className="font-700 text-blue-700 hover:underline notranslate">AirlinesAirports.com</a>.</p>
      </div>
    </footer>
  );
}
